import { useEffect, useState } from "react";
import { getRooms, getCautions, setCaution } from "../../firebase/services";
import { Badge, Avatar, Spinner, EmptyState, Modal, Btn } from "../../components/UI";

const LOYER = 27500;

const CAUTION_STATUS = {
  pending:  { label: "Non payée",  type: "late",  icon: "⏳" },
  paid:     { label: "Payée ✓",   type: "ok",    icon: "✅" },
  returned: { label: "Remboursée", type: "info",  icon: "💰" },
  partial:  { label: "Partielle",  type: "warn",  icon: "⚠️" },
};

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function OwnerCautions() {
  const [rooms, setRooms]       = useState([]);
  const [cautions, setCautions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("all");
  const [modal, setModal]       = useState(null); // room object
  const [form, setForm]         = useState({ status: "paid", amount: String(LOYER), note: "" });
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    const unsubs = [
      getRooms(data => { setRooms(data); setLoading(false); }),
      getCautions(setCautions),
    ];
    return () => unsubs.forEach(u => u && u());
  }, []);

  const cautionFor = (roomId) => cautions.find(c => c.roomId === roomId);
  const pendingCautions = cautions.filter(c => c.status === "pending_confirmation");

  const openModal = (room) => {
    const c = cautionFor(room.id);
    setForm({
      status: c?.status || "pending",
      amount: String(c?.amount || LOYER),
      note: c?.note || "",
    });
    setModal(room);
  };

  const handleSave = async () => {
    if (!modal) return;
    setSaving(true);
    await setCaution(modal.id, {
      status: form.status,
      amount: parseInt(form.amount) || LOYER,
      note: form.note.trim(),
      returnedAt: form.status === "returned" ? new Date() : null,
    });
    setSaving(false);
    setModal(null);
  };

  // Stats
  const paid     = rooms.filter(r => cautionFor(r.id)?.status === "paid").length;
  const pending  = rooms.filter(r => !cautionFor(r.id) || cautionFor(r.id)?.status === "pending").length;
  const returned = rooms.filter(r => cautionFor(r.id)?.status === "returned").length;
  const totalCollected = cautions.filter(c => c.status === "paid").reduce((sum, c) => sum + (c.amount || LOYER), 0);

  const filtered = rooms.filter(r => {
    if (!r.tenantName) return false; // ignore vacant rooms
    const c = cautionFor(r.id);
    const status = c?.status || "pending";
    if (filter === "pending")  return status === "pending";
    if (filter === "paid")     return status === "paid";
    if (filter === "returned") return status === "returned";
    return true;
  }).filter(r => r.tenantName); // only occupied rooms

  if (loading) return <div style={S.page}><Spinner /></div>;

  return (
    <div style={S.page}>
      <div style={S.topBar}>
        <div>
          <h1 style={S.title}>Cautions</h1>
          <p style={S.sub}>Garanties de 1 mois de loyer</p>
        </div>
      </div>

      {/* Stats */}
      <div style={S.statsGrid}>
        <div style={S.statBox}>
          <span style={S.statNum}>{paid}</span>
          <span style={S.statLabel}>Payées</span>
        </div>
        <div style={{ ...S.statBox, borderColor: "#f5b8b8" }}>
          <span style={{ ...S.statNum, color: "#a32d2d" }}>{pending}</span>
          <span style={S.statLabel}>Non payées</span>
        </div>
        <div style={{ ...S.statBox, borderColor: "#d0dce8" }}>
          <span style={{ ...S.statNum, color: "#5a7a9a" }}>{returned}</span>
          <span style={S.statLabel}>Remboursées</span>
        </div>
      </div>

      {/* Cautions de réservation en attente */}
      {pendingCautions.length > 0 && (
        <div style={{ margin:"0 16px 10px", background:"#fff3e0", border:"1.5px solid #f5d8a0", borderRadius:12, padding:"12px 14px" }}>
          <p style={{ fontSize:13, fontWeight:700, color:"#8a4c00", margin:"0 0 8px" }}>
            🏦 {pendingCautions.length} caution{pendingCautions.length>1?"s":""} de réservation à confirmer
          </p>
          {pendingCautions.map(c => {
            const r = rooms.find(room => room.id === c.roomId);
            return (
              <div key={c.id} style={{ background:"white", borderRadius:10, padding:"10px 12px", marginBottom:6, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <p style={{ fontSize:13, fontWeight:600, color:"#1a2a3a", margin:0 }}>Chambre {r?.number || c.roomId}</p>
                  <p style={{ fontSize:11, color:"#7a90a8", margin:0 }}>{c.method} · {c.reference || "—"}</p>
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <button style={{ padding:"6px 10px", borderRadius:8, background:"#e8f5e0", color:"#2d6a0a", border:"none", fontSize:12, fontWeight:700, cursor:"pointer" }}
                    onClick={() => setCaution(c.roomId, { status:"paid", amount:c.amount, note:`Caution réservation. ${c.method} ${c.reference}` })}>
                    ✓ Confirmer
                  </button>
                  <button style={{ padding:"6px 10px", borderRadius:8, background:"#fdecea", color:"#a32d2d", border:"none", fontSize:12, fontWeight:700, cursor:"pointer" }}
                    onClick={() => setCaution(c.roomId, { status:"pending", amount:c.amount, note:"Caution non confirmée" })}>
                    ✕ Rejeter
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Total collecté */}
      <div style={S.totalCard}>
        <div>
          <p style={S.totalLabel}>Total cautions collectées</p>
          <p style={S.totalAmount}>{totalCollected.toLocaleString("fr-FR")} FCFA</p>
        </div>
        <span style={{ fontSize: 32 }}>🏦</span>
      </div>

      {/* Filtres */}
      <div style={S.filterBar}>
        {[["all","Toutes"],["pending","Non payées"],["paid","Payées"],["returned","Remboursées"]].map(([v,l]) => (
          <button key={v} style={{ ...S.pill, ...(filter === v ? S.pillOn : {}) }} onClick={() => setFilter(v)}>{l}</button>
        ))}
      </div>

      <div style={S.scroll}>
        {filtered.length === 0 ? (
          <EmptyState icon="🏦" title="Aucune chambre occupée" subtitle="Les cautions apparaissent pour les chambres avec locataires" />
        ) : filtered.map((room, i) => {
          const c   = cautionFor(room.id);
          const st  = CAUTION_STATUS[c?.status || "pending"];
          return (
            <div key={room.id} style={S.card} onClick={() => openModal(room)}>
              <div style={S.cardTop}>
                <Avatar name={room.tenantName} size={40} index={i} />
                <div style={{ flex: 1 }}>
                  <p style={S.roomNum}>Chambre {room.number}</p>
                  <p style={S.tenant}>{room.tenantName}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={S.amount}>{(c?.amount || LOYER).toLocaleString("fr-FR")} FCFA</p>
                  <Badge type={st.type}>{st.icon} {st.label}</Badge>
                </div>
              </div>
              {c?.note && <p style={S.note}>💬 {c.note}</p>}
              {c?.status === "paid" && c?.createdAt && (
                <p style={S.dateInfo}>Enregistrée le {formatDate(c.createdAt)}</p>
              )}
              {c?.status === "returned" && c?.returnedAt && (
                <p style={S.dateInfo}>Remboursée le {formatDate(c.returnedAt)}</p>
              )}
              <p style={S.tapHint}>Appuyer pour modifier →</p>
            </div>
          );
        })}
      </div>

      {/* Modal édition caution */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={`Caution — Ch. ${modal?.number}`}>
        {modal && (
          <div>
            <div style={S.modalInfo}>
              <p style={{ fontSize: 13, fontWeight: 700, color: "#1a2a3a", margin: "0 0 2px" }}>{modal.tenantName}</p>
              <p style={{ fontSize: 12, color: "#5a7a9a", margin: 0 }}>Chambre {modal.number}</p>
            </div>

            <p style={S.fieldLabel}>Statut de la caution</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
              {Object.entries(CAUTION_STATUS).map(([key, val]) => (
                <button
                  key={key}
                  style={{
                    padding: "10px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600,
                    border: form.status === key ? "1.5px solid #2e86c1" : "1.5px solid #d0dce8",
                    background: form.status === key ? "#e3f1fb" : "white",
                    color: form.status === key ? "#1a3a5c" : "#5a7a9a",
                    cursor: "pointer", fontFamily: "inherit", textAlign: "center"
                  }}
                  onClick={() => setForm(f => ({ ...f, status: key }))}
                >
                  {val.icon}<br />{val.label}
                </button>
              ))}
            </div>

            <p style={S.fieldLabel}>Montant (FCFA)</p>
            <input
              style={S.input}
              type="number"
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
            />

            <p style={S.fieldLabel}>Note (optionnel)</p>
            <textarea
              style={{ ...S.input, minHeight: 60, resize: "none" }}
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Ex: Payé en espèces le 01/06/2026..."
            />

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
              <Btn variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? "Enregistrement..." : "✓ Enregistrer"}
              </Btn>
              <Btn variant="ghost" onClick={() => setModal(null)}>Annuler</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

const S = {
  page:     { display: "flex", flexDirection: "column", height: "100%", background: "#f5f8fc", fontFamily: "'Barlow',sans-serif" },
  topBar:   { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px 6px" },
  title:    { fontSize: 22, fontWeight: 800, color: "#1a3a5c", fontFamily: "'Barlow Condensed',sans-serif", margin: 0 },
  sub:      { fontSize: 12, color: "#7a90a8", margin: "2px 0 0" },
  statsGrid:{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, padding: "8px 16px" },
  statBox:  { background: "white", borderRadius: 12, padding: "10px 8px", textAlign: "center", border: "0.5px solid #d0dce8" },
  statNum:  { display: "block", fontSize: 22, fontWeight: 800, color: "#2d6a0a", fontFamily: "'Barlow Condensed',sans-serif" },
  statLabel:{ display: "block", fontSize: 10, color: "#7a90a8", marginTop: 2 },
  totalCard:{ margin: "8px 16px", background: "linear-gradient(135deg,#1a3a5c,#2e6aa0)", borderRadius: 14, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  totalLabel: { fontSize: 12, color: "#90bde0", margin: "0 0 2px" },
  totalAmount:{ fontSize: 22, fontWeight: 800, color: "white", margin: 0, fontFamily: "'Barlow Condensed',sans-serif" },
  filterBar:{ display: "flex", gap: 8, padding: "8px 16px 10px", overflowX: "auto" },
  pill:     { padding: "6px 12px", borderRadius: 20, border: "1.5px solid #d0dce8", background: "white", color: "#5a7a9a", fontSize: 11, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" },
  pillOn:   { background: "#1a3a5c", color: "white", borderColor: "#1a3a5c" },
  scroll:   { flex: 1, overflowY: "auto", padding: "0 16px 80px" },
  card:     { background: "white", borderRadius: 14, padding: "14px", marginBottom: 10, border: "0.5px solid #d0dce8", cursor: "pointer" },
  cardTop:  { display: "flex", alignItems: "center", gap: 10, marginBottom: 6 },
  roomNum:  { fontSize: 14, fontWeight: 700, color: "#1a3a5c", margin: 0 },
  tenant:   { fontSize: 12, color: "#5a7a9a", margin: 0 },
  amount:   { fontSize: 13, fontWeight: 700, color: "#1a3a5c", margin: "0 0 4px", fontFamily: "'Barlow Condensed',sans-serif" },
  note:     { fontSize: 12, color: "#5a7a9a", fontStyle: "italic", margin: "4px 0 0" },
  dateInfo: { fontSize: 11, color: "#aab8c8", margin: "2px 0 0" },
  tapHint:  { fontSize: 10, color: "#aab8c8", textAlign: "right", margin: "6px 0 0" },
  modalInfo:{ background: "#f0f5fa", borderRadius: 12, padding: "10px 14px", marginBottom: 14 },
  fieldLabel:{ fontSize: 12, color: "#4a6a8a", fontWeight: 500, marginBottom: 8 },
  input:    { width: "100%", border: "1.5px solid #d0dce8", borderRadius: 10, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", background: "#f7fafd", color: "#1a2a3a", outline: "none", boxSizing: "border-box", marginBottom: 14 },
};
